/**
 * Animated chat demo scripts.
 *
 * One per page. Each script assumes the parent has already shared their
 * child profile during onboarding, so the parent's typed message is about
 * the moment only. TopParent's reply leads with a friendly intro line,
 * then a matching panel, then a structured activity card.
 */

export const HOME_SCRIPT = {
    parentMessage:
        "We have 10 minutes before dinner. He has loads of energy and only cushions nearby.",
    introReply:
        "Got it. Here is an activity that will work well for him today. It uses his energy and only needs what is already near you.",
    matchingRows: [
        { label: "From profile", value: "Age 4 \u00B7 loves movement" },
        { label: "Today", value: "high energy \u00B7 before dinner" },
        { label: "Time", value: "10 minutes" },
        { label: "Materials", value: "cushions" },
        { label: "Goal", value: "movement + regulation" },
    ],
    activity: {
        title: "Floor Is Lava",
        summary: "10 minutes \u00B7 Cushions or folded towels \u00B7 Movement + planning",
        whyFits:
            "Uses his energy without a big setup. Moving between cushions helps him plan, balance, and slow down through play.",
        steps: [
            "Place cushions across the floor as stepping stones.",
            "Choose a start and finish point.",
            "Your child crosses without touching the floor.",
            "Move the cushions closer or further apart to adjust the challenge.",
            "Reset the path and play again.",
        ],
    },
    actions: ["Try this", "Make it calmer", "Use fewer cushions"],
    caption: "Example flow. The activity flow is being built now.",
};

export const HOW_IT_WORKS_SCRIPT = {
    parentMessage:
        "We have 15 minutes before dinner. Indoor, medium energy, crayons and paper nearby.",
    introReply:
        "Here is something short and low-prep, easy to stop when dinner is ready.",
    matchingRows: [
        { label: "From profile", value: "Age 5 \u00B7 loves drawing" },
        { label: "Today", value: "medium energy \u00B7 before dinner" },
        { label: "Time", value: "15 minutes" },
        { label: "Materials", value: "crayons and paper" },
        { label: "Goal", value: "fine motor + connection" },
    ],
    activity: {
        title: "Copy-Me Drawing",
        summary: "15 minutes \u00B7 Crayons and paper \u00B7 Fine motor play",
        whyFits:
            "A calm, side-by-side activity that turns a few minutes before dinner into a small shared moment.",
        steps: [
            "Sit side by side with one piece of paper each.",
            "Draw one simple shape and invite your child to copy it.",
            "Let them change it, add to it, or turn it into something silly.",
            "Take turns starting a new shape.",
            "Stop when it feels complete or when dinner is ready.",
        ],
    },
    actions: ["Try this", "Make it calmer", "Use one crayon each"],
    caption: "Example preview. Parents stay in control of what to try, adapt, or skip.",
};

export const NEURODIVERSE_SCRIPT = {
    parentMessage:
        "Tired today. We have 8 minutes. Too many choices can feel overwhelming.",
    introReply:
        "Here is something gentle. Two items only, with room to pause if that already feels like enough.",
    matchingRows: [
        { label: "From profile", value: "Age 4 \u00B7 sensory-aware" },
        { label: "Today", value: "low energy \u00B7 needs calm" },
        { label: "Time", value: "8 minutes" },
        { label: "Materials", value: "one soft and one smooth item" },
        { label: "Goal", value: "calm + sensory exploration" },
    ],
    activity: {
        title: "Two-Texture Choice",
        summary: "8 minutes \u00B7 Two items \u00B7 Sensory-aware play",
        whyFits:
            "Only two choices keeps things calm. Your child can touch, point, name, or simply choose one, at their own pace.",
        steps: [
            "Pick one soft item and one smooth item.",
            "Place them side by side on a flat surface.",
            "Let your child touch each one in their own time.",
            "Invite them to choose a favourite, name it, or pass it back.",
            "Pause whenever your child seems ready to stop.",
        ],
    },
    actions: ["Try this", "Use only one item", "Make it shorter"],
    caption: "Example preview. Every idea can be adapted to your child's comfort and pace.",
};

export const WHY_SCRIPT = {
    parentMessage:
        "Quiet moment before bed. Nothing messy or exciting.",
    introReply:
        "Here is something simple and slow. No materials, just one quiet exchange before sleep.",
    matchingRows: [
        { label: "From profile", value: "Age 3 \u00B7 loves stories" },
        { label: "Today", value: "calm \u00B7 bedtime" },
        { label: "Time", value: "5 minutes" },
        { label: "Materials", value: "none" },
        { label: "Goal", value: "bonding + wind-down" },
    ],
    activity: {
        title: "One-Line Story",
        summary: "5 minutes \u00B7 No materials \u00B7 Bonding moment",
        whyFits:
            "A small back-and-forth that turns the end of the day into a calm shared moment, with nothing to set up or clear away.",
        steps: [
            "Say one simple sentence about the day.",
            "Invite your child to add one word, sound, or gesture.",
            "Take turns adding one small thing each time.",
            "Keep the pace slow and your voice quiet.",
            "Stop whenever it feels complete.",
        ],
    },
    actions: ["Try this", "Make it even slower", "Try one word each"],
    caption: "Example preview. A small activity can become a calm moment together.",
};
